import _cloneDeep from 'lodash/cloneDeep';
import { isMobileLayout } from 'services/utils';
import { UPDATE_DEVICE } from 'services/Permissions';

const INPUT_FOR_KEY_ID = 'add-label-input-for-key';

/* @ngInject */
export default class ThingDetailLabelsController {
    constructor(EventEmitter, DialogService, Loader, $timeout, AnalyticsService, RoleManager, SensorService) {
        this.EventEmitter = EventEmitter;
        this.DialogService = DialogService;
        this.Loader = Loader;
        this.$timeout = $timeout;
        this.AnalyticsService = AnalyticsService;
        this.RoleManager = RoleManager;
        this.SensorService = SensorService;
        this.uniqueId = Math.random().toString(36).substr(2, 9);
    }

    get canUpdate() {
        return this.RoleManager.can(UPDATE_DEVICE);
    }

    get hasLabels() {
        return this.labels ? Object.keys(this.labels).length : false;
    }

    $onChanges(changes) {
        if (changes.labels) {
            this.originalLabels = _cloneDeep(changes.labels.currentValue);
        }
    }

    $onInit() {
        this.labelKey = '';
        this.labelValue = '';
        this.inputForKeyId = `${INPUT_FOR_KEY_ID}-${this.uniqueId}`;
        this.editMode = false
        this.isMobile = isMobileLayout();
        
        // Keys dropdown
        this.showKeysDropdown = false;
        this.filteredLabelKeys = [];
        this.selectedKeyIndex = -1;
        
        // Values dropdown (for new label)
        this.showValuesDropdown = false;
        this.filteredLabelValues = [];
        this.selectedValueIndex = -1;
        
        // Values dropdown (for existing labels)
        this.showExistingValuesDropdown = [];
        this.filteredExistingLabelValues = [];
        this.selectedExistingValueIndex = [];
        this.availableExistingLabelValues = [];
        
        // Set default value for alwaysEditable if not provided
        if (this.alwaysEditable === null) {
            this.alwaysEditable = false;
        }

        if (this.alwaysEditable) {
            this.editMode = true;
        }
    }

    getCountDisplayText(count, isProject = false) {
        if (isProject) {
            return null; // No count text for project context
        }
        return `Set on ${count} device${count === 1 ? '' : 's'}`;
    }

    isProjectContext() {
        return this.labelContext === 'project';
    }

    addLabel() {
        this.labelInProgress = !this.labelInProgress;
    }

    enterEditMode() {
        this.editMode = !this.editMode
    }

    isInvalidFilterKey(key) {
        return Boolean(this.validateFilterKeys) && /\s/.test(key);
    }

    createLabel() {
        const key = (this.labelKey || '').trim();
        if (!key) {
            return;
        }
        if (this.isInvalidFilterKey(key)) {
            this.keyError = 'Label keys cannot contain spaces';
            return;
        }
        this.keyError = null;

        this.AnalyticsService.trackEvent("device.label.added");

        this.Loader.promise = this.onLabelAdd(this.EventEmitter({
            label: {
                key,
                value: this.labelValue
            }
        })).then(() => {
            this.labelInProgress = false;

            this.labelKey = '';
            this.labelValue = '';

            this.$timeout(() => {
                const input = document.getElementById(this.inputForKeyId);
                if (input) {
                    input.focus();
                }
            }, 0);
        });
    }

    saveValue(labelKey, value) {
        if (value !== this.originalLabels[labelKey]) {
            this.originalLabels[labelKey] = value
            this.AnalyticsService.trackEvent("device.label.updated");
            this.labelsPromise = this.onLabelChange(this.EventEmitter({
                labelKey,
                value
            }));
        }
    }

    showDeleteConfirmation(key) {
        this.DialogService.confirm({
            title: 'Remove label?',
            textContent: `Do you really want to remove label "${key}"?`,
            ariaLabel: 'remove-label',
            ok: 'Remove',
            cancel: 'Cancel'
        }).then(() => {
            this.remove(key);
        }).catch(() => {});
    }

    remove(labelKey) {
        this.AnalyticsService.trackEvent("device.label.removed");
        this.labelsPromise = this.onLabelRemove(this.EventEmitter({
            labelKey
        }));
    }

    onKeyInputFocus() {
        if (this.availableLabelKeys && this.availableLabelKeys.length > 0) {
            this.filterLabelKeys();
            this.showKeysDropdown = true;
        }
    }

    onKeyInputBlur() {
        // Delay hiding dropdown to allow clicking on options
        this.$timeout(() => {
            this.showKeysDropdown = false;
        }, 200);
    }

    onKeyInputChange() {
        this.keyError = null;
        if (this.availableLabelKeys && this.availableLabelKeys.length > 0) {
            this.filterLabelKeys();
            this.showKeysDropdown = true;
        }
    }

    filterLabelKeys() {
        if (!this.availableLabelKeys) {
            this.filteredLabelKeys = [];
            return;
        }

        const query = (this.labelKey || '').toLowerCase();
        this.filteredLabelKeys = this.availableLabelKeys.filter(item => 
            item.key.toLowerCase().includes(query)
        ).slice(0, 100); // Limit to 100 results
        this.selectedKeyIndex = -1; // Reset selection when filtering
    }

    selectLabelKey(labelKey) {
        this.labelKey = labelKey;
        this.showKeysDropdown = false;
        // Focus on the value input after selecting a key
        this.$timeout(() => {
            const valueInput = document.querySelector(`#${this.inputForKeyId}`).closest('tr').querySelector('td:nth-child(2) input');
            if (valueInput) {
                valueInput.focus();
            }
        }, 0);
    }

    onValueInputFocus() {
        if (this.labelKey && this.labelKey.trim()) {
            this.fetchLabelValues();
        }
    }

    onValueInputBlur() {
        // Delay hiding dropdown to allow clicking on options
        this.$timeout(() => {
            this.showValuesDropdown = false;
        }, 200);
    }

    onValueInputChange() {
        if (this.labelKey && this.labelKey.trim()) {
            this.filterLabelValues();
            this.showValuesDropdown = this.filteredLabelValues.length > 0;
        }
    }

    fetchLabelValues() {
        if (!this.labelKey || !this.labelKey.trim()) {
            return;
        }

        this.SensorService.listLabelValues(this.labelKey.trim()).then(response => {
            // Extract label values from both device and project label values
            this.availableLabelValues = [];
            if (response.deviceLabelValues) {
                this.availableLabelValues.push(...response.deviceLabelValues.map(item => ({
                    value: item.value,
                    deviceCount: item.deviceCount,
                    type: 'device'
                })));
            }
            if (response.projectLabelValues) {
                this.availableLabelValues.push(...response.projectLabelValues.map(item => ({
                    value: item.value,
                    deviceCount: item.deviceCount,
                    type: 'project'
                })));
            }
            // Sort by device count (descending) to show most used values first
            this.availableLabelValues.sort((a, b) => b.deviceCount - a.deviceCount);
            
            this.filterLabelValues();
            this.showValuesDropdown = this.filteredLabelValues.length > 0;
        }).catch(error => {
            console.error('Error fetching label values:', error); // eslint-disable-line no-console
            this.availableLabelValues = [];
            this.filteredLabelValues = [];
            this.showValuesDropdown = false;
        });
    }

    filterLabelValues() {
        if (!this.availableLabelValues) {
            this.filteredLabelValues = [];
            return;
        }

        const query = (this.labelValue || '').toLowerCase();
        this.filteredLabelValues = this.availableLabelValues.filter(item => 
            item.value.toLowerCase().includes(query)
        ).slice(0, 100); // Limit to 100 results
        this.selectedValueIndex = -1; // Reset selection when filtering
    }

    selectLabelValue(labelValue) {
        this.labelValue = labelValue;
        this.showValuesDropdown = false;
    }

    onExistingValueInputFocus(labelKey, index) {
        if (labelKey && labelKey.trim()) {
            this.fetchExistingLabelValues(labelKey, index);
        }
    }

    onExistingValueInputBlur(index) {
        // Delay hiding dropdown to allow clicking on options
        this.$timeout(() => {
            this.showExistingValuesDropdown[index] = false;
        }, 200);
    }

    onExistingValueInputChange(labelKey, index) {
        if (labelKey && labelKey.trim()) {
            this.filterExistingLabelValues(index);
            this.showExistingValuesDropdown[index] = this.filteredExistingLabelValues[index] && this.filteredExistingLabelValues[index].length > 0;
        }
    }

    fetchExistingLabelValues(labelKey, index) {
        if (!labelKey || !labelKey.trim()) {
            return;
        }

        this.SensorService.listLabelValues(labelKey.trim()).then(response => {
            // Extract label values from both device and project label values
            this.availableExistingLabelValues[index] = [];
            if (response.deviceLabelValues) {
                this.availableExistingLabelValues[index].push(...response.deviceLabelValues.map(item => ({
                    value: item.value,
                    deviceCount: item.deviceCount,
                    type: 'device'
                })));
            }
            if (response.projectLabelValues) {
                this.availableExistingLabelValues[index].push(...response.projectLabelValues.map(item => ({
                    value: item.value,
                    deviceCount: item.deviceCount,
                    type: 'project'
                })));
            }
            // Sort by device count (descending) to show most used values first
            this.availableExistingLabelValues[index].sort((a, b) => b.deviceCount - a.deviceCount);
            
            this.filterExistingLabelValues(index);
            this.showExistingValuesDropdown[index] = this.filteredExistingLabelValues[index] && this.filteredExistingLabelValues[index].length > 0;
        }).catch(error => {
            console.error('Error fetching existing label values:', error); // eslint-disable-line no-console
            this.availableExistingLabelValues[index] = [];
            this.filteredExistingLabelValues[index] = [];
            this.showExistingValuesDropdown[index] = false;
        });
    }

    filterExistingLabelValues(index) {
        if (!this.availableExistingLabelValues[index]) {
            this.filteredExistingLabelValues[index] = [];
            return;
        }

        // Get current value from the labels object
        const labelKeys = Object.keys(this.labels);
        const currentValue = this.labels[labelKeys[index]] || '';
        const query = currentValue.toLowerCase();
        
        this.filteredExistingLabelValues[index] = this.availableExistingLabelValues[index].filter(item => 
            item.value.toLowerCase().includes(query)
        ).slice(0, 100); // Limit to 100 results
        this.selectedExistingValueIndex[index] = -1; // Reset selection when filtering
    }

    selectExistingLabelValue(labelValue, labelKey, index) {
        // Update the value in the labels object
        this.labels[labelKey] = labelValue;
        this.showExistingValuesDropdown[index] = false;
        // Save the value immediately
        this.saveValue(labelKey, labelValue);
    }
}
